/**
 * PricingPlans — the Smart / Boost / Ultimo tier cards (rk- design): the
 * Monthly / Yearly segment with the gliding thumb, the price swap, the limits,
 * the Apps + Channels logo rows with the "+N" popover, and the feature list.
 *
 * Extracted 1:1 from the per-agent billing page (BotBilling.js) so the SAME
 * component renders there and inside the agent-creation flow's plan modal.
 * Data-driven from the bot_subscription_plans rows (GET /api/subscription_plans);
 * TIER_ORDER + the highlighted tier are the only things decided in code.
 * Styles: BotBilling.css (rk-*), tokens on the surrounding .bbp-page.
 *
 * Props
 *   plans               raw plan rows (monthly + yearly)
 *   billingCycle        'month' | 'year'      onBillingCycleChange(cycle)
 *   currentTier         plan name or null     currentInterval 'month' | 'year'
 *   onPaidStripe        true when the agent has a real Stripe subscription
 *   isCancelling / endsText   cancel-at-period-end state (paid Stripe only)
 *   pending / busy      in-flight action key / any action running
 *   onChoose(row, key)  Choose plan / switch interval
 *   onPortal(key)       cancel / reactivate (Stripe customer portal)
 */
import { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSpinner, faCheck, faMinus, faComments, faBookOpen, faSliders, faPuzzlePiece,
  faCircleExclamation, faCode, faLink, faBolt,
} from "@fortawesome/free-solid-svg-icons";
import { faTelegram } from "@fortawesome/free-brands-svg-icons";
import SlackLogo from "./SlackLogo.js";
import { CHANNEL_GUIDES } from "../data/channelGuides.js";
import "../pages/BotBilling.css";

export const TIER_ORDER = ["Smart", "Boost", "Ultimo"];
const LOGOS_SHOWN = 5;

// Connectable apps (integrations the agent can act with) — image logos in /public.
const APPS = [
  { key: "calendly", label: "Calendly", logo: "/calendly-logo.png" },
  { key: "hubspot", label: "HubSpot", logo: "/hubspot-logo.png" },
  { key: "stripe", label: "Stripe", logo: "/stripe-logo.png" },
  { key: "mailchimp", label: "Mailchimp", logo: "/mailchimp-logo.png" },
  { key: "calcom", label: "Cal.com", logo: "/calcom-logo.png" },
  { key: "zendesk", label: "Zendesk", logo: "/zendesk-logo.png" },
];

// Deploy channels (where the agent goes live). All five messaging channels lead,
// matching the Wix and WordPress plan cards; the remaining website platforms
// from the Channels page fold into the "+N" popover.
const BASE_CHANNELS = [
  { key: "instagram", label: "Instagram", logo: "/instagram-logo.webp" },
  { key: "facebook", label: "Facebook", logo: "/facebook-logo.webp" },
  { key: "whatsapp", label: "WhatsApp", logo: "/whatsapp-logo.webp" },
  { key: "slack", label: "Slack", logoComponent: SlackLogo },
  { key: "telegram", label: "Telegram", icon: faTelegram, color: "#229ED9" },
  { key: "website", labelKey: "billing_channel_website_widget", icon: faCode },
  { key: "sharelink", labelKey: "billing_channel_chat_link", icon: faLink },
  { key: "wix", label: "Wix", logo: "/wix-logo.png" },
  { key: "webflow", label: "Webflow", logo: "/webflow-logo.png" },
  { key: "wordpress", label: "WordPress", logo: "/wordpress-logo.png" },
];

// Every website builder / e-commerce / CMS platform from the Channels page,
// reusing its real brand logo (single source of truth: channelGuides.js).
const GUIDE_CHANNELS = CHANNEL_GUIDES.map((g) => ({
  key: g.key,
  label: g.name,
  logo: g.logo,
}));

const CHANNELS = [...BASE_CHANNELS, ...GUIDE_CHANNELS];

// Leading "no limit" chips, shown inline with the logos (same chip style):
// any website can be embedded, any app can be connected via API / webhooks.
const CHANNEL_ANY = { key: "any-web", icon: faCode, labelKey: "billing_chip_any_website", color: "#5616ea" };
const APP_ANY = { key: "any-app", icon: faBolt, labelKey: "billing_chip_any_app", color: "#5616ea" };

function LogoChip({ item }) {
  const { t } = useTranslation();
  const label = item.labelKey ? t(item.labelKey) : item.label;
  return (
    <span className="rk-logo" data-tip={label} aria-label={label}>
      {item.logoComponent ? (
        <item.logoComponent size={19} />
      ) : item.logo ? (
        <img src={item.logo} alt={label} />
      ) : (
        <FontAwesomeIcon icon={item.icon} style={item.color ? { color: item.color } : undefined} />
      )}
    </span>
  );
}

// A row of the most important logos with a "+N" chip. The popover (listing only
// the hidden items) opens while the pointer is on the chip OR the popover — a
// small close delay lets the mouse travel chip -> popover without flicker, and
// hovering a sibling logo closes it.
function LogoRow({ items, lead, max = LOGOS_SHOWN }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  // The popover opens downwards by default; when it would run past the bottom
  // of its scroll container (or the viewport) it flips upwards instead, so it
  // never adds scroll height (e.g. inside a modal).
  const [flipUp, setFlipUp] = useState(false);
  const timer = useRef(null);
  const wrapRef = useRef(null);
  const shown = items.slice(0, max);
  const extra = items.length - max;
  const hidden = items.slice(max);

  const cancelClose = () => { if (timer.current) { clearTimeout(timer.current); timer.current = null; } };
  const scheduleClose = () => { cancelClose(); timer.current = setTimeout(() => setOpen(false), 90); };
  useEffect(() => () => cancelClose(), []);

  useLayoutEffect(() => {
    if (!open || !wrapRef.current) return;
    const pop = wrapRef.current.querySelector(".rk-logo-pop");
    if (!pop) return;
    // Nearest vertical scroll container decides the available space.
    let el = wrapRef.current.parentElement;
    let limit = window.innerHeight;
    while (el && el !== document.body) {
      const oy = getComputedStyle(el).overflowY;
      if (oy === "auto" || oy === "scroll") { limit = el.getBoundingClientRect().bottom; break; }
      el = el.parentElement;
    }
    const chip = wrapRef.current.getBoundingClientRect();
    const needed = pop.offsetHeight + 8;
    setFlipUp(chip.bottom + needed > limit - 8 && chip.top - needed > 0);
  }, [open]);

  return (
    <div className="rk-logo-row">
      <div className="rk-logos">
        {lead && <LogoChip key={lead.key} item={lead} />}
        {shown.map((it) => <LogoChip key={it.key} item={it} />)}
        {extra > 0 && (
          <span className="rk-logo-more-wrap" ref={wrapRef}>
            <button
              type="button"
              className="rk-logo-more"
              onMouseEnter={() => { cancelClose(); setOpen(true); }}
              onMouseLeave={scheduleClose}
              onClick={() => setOpen((o) => !o)}
              aria-expanded={open}
              aria-label={t("billing_show_n_more", { count: extra })}
            >
              +{extra}
            </button>
            {open && (
              <div
                className={`rk-logo-pop${flipUp ? " up" : ""}`}
                role="tooltip"
                onMouseEnter={cancelClose}
                onMouseLeave={scheduleClose}
              >
                {hidden.map((it) => (
                  <span key={it.key} className="rk-logo-pop-item">
                    <LogoChip item={it} />
                    <span className="rk-logo-pop-label">{it.labelKey ? t(it.labelKey) : it.label}</span>
                  </span>
                ))}
              </div>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

export default function PricingPlans({
  plans,
  billingCycle,
  onBillingCycleChange,
  currentTier = null,
  currentInterval = "month",
  onPaidStripe = false,
  isCancelling = false,
  endsText = "",
  pending = null,
  busy = false,
  onChoose,
  onPortal,
  creditsLabel = false, // Wix apps sold in AI credits name the first limit that way
}) {
  const { t } = useTranslation();

  const monthlyByName = useMemo(() => {
    const m = {};
    plans.forEach((p) => { if (p.billing_interval !== "year") m[p.name] = p; });
    return m;
  }, [plans]);
  const yearlyByName = useMemo(() => {
    const m = {};
    plans.forEach((p) => { if (p.billing_interval === "year") m[p.name] = p; });
    return m;
  }, [plans]);
  const tiers = useMemo(
    () => TIER_ORDER.map((n) => monthlyByName[n]).filter(Boolean),
    [monthlyByName]
  );
  const savingsFor = useCallback((name) => {
    const m = monthlyByName[name];
    const y = yearlyByName[name];
    if (m && y && m.monthly_price > 0) {
      return {
        pct: Math.round(((m.monthly_price - y.monthly_price) / m.monthly_price) * 100),
        yearlyMonthly: y.monthly_price,
        monthly: m.monthly_price,
      };
    }
    return null;
  }, [monthlyByName, yearlyByName]);

  return (
    <div className="rk-billing">
      <div className="rk-billing-toggle">
        <span className={`rk-billing-thumb${billingCycle === "year" ? " right" : ""}`} aria-hidden />
        <button
          type="button"
          className={billingCycle === "month" ? "active" : ""}
          onClick={() => onBillingCycleChange("month")}
        >
          {t("monthly")}
        </button>
        <button
          type="button"
          className={billingCycle === "year" ? "active" : ""}
          onClick={() => onBillingCycleChange("year")}
        >
          {t("yearly")}
        </button>
      </div>

      <div className="rk-plans">
        {tiers.map((mRow) => {
          const name = mRow.name;
          const yRow = yearlyByName[name];
          const row = billingCycle === "year" ? (yRow || mRow) : mRow;
          const sv = savingsFor(name);
          const highlighted = name === "Boost";
          const isCurrentTier = currentTier === name;
          const isCurrentExact = isCurrentTier && currentInterval === billingCycle;

          const messages = (mRow.message_limit ?? 0).toLocaleString("de-CH");
          const docs = (mRow.document_limit ?? 0).toLocaleString("de-CH");
          const skillCount = mRow.skill_limit ?? 0;
          const behaviors = mRow.behavior_limit ?? 0;
          const rescrape = mRow.automatic_rescraping; // 'none' | 'daily' | 'monthly'
          const removeBranding = !!mRow.remove_branding;

          // Primary CTA: current / switch interval / choose (checkout|portal).
          const cardKey = `card:${name}`;
          // Only emphasise the recommended (Boost) tier while the user has
          // no current plan; once they're on a tier, nothing should look
          // "recommended" except the current plan's own styling.
          const showFeatured = highlighted && !currentTier;
          let label, action, ctaDisabled = busy, ctaVariant = showFeatured ? "featured" : "";
          if (isCurrentExact) {
            // No disabled "Current plan" button — the flag already marks it.
            label = null;
          } else if (isCurrentTier) {
            label = billingCycle === "year" ? t("billing_switch_to_yearly") : t("billing_switch_to_monthly");
            // Paid Stripe → portal; internal/comped → in-app Checkout.
            action = () => onChoose(row, cardKey);
          } else {
            label = t("billing_choose_plan");
            action = () => onChoose(row, cardKey);
          }

          return (
            <div
              key={name}
              className={`rk-plan${showFeatured ? " featured" : ""}${isCurrentTier ? " current" : ""}`}
            >
              {isCurrentTier ? (
                <span className="rk-plan-flag">
                  <FontAwesomeIcon icon={faCheck} style={{ fontSize: 9, marginRight: 5 }} />{t("current_plan")}
                </span>
              ) : showFeatured ? (
                // Once the user is on any paid tier, drop the "Recommended"
                // flag — the only badge that should show then is "Current plan".
                <span className="rk-plan-flag">{t("recommended")}</span>
              ) : null}

              <div className="rk-plan-name">{name}</div>

              {billingCycle === "year" && sv ? (
                <div key={billingCycle}>
                  <div className="rk-plan-price">
                    <s className="rk-plan-old">${sv.monthly}</s>
                    <span className="rk-plan-arrow">→</span>
                    ${sv.yearlyMonthly}
                    <span className="rk-plan-save">−{sv.pct}%</span>
                  </div>
                  <div className="rk-plan-billed">{t("billing_per_month_billed_annually")}</div>
                </div>
              ) : (
                <div className="rk-plan-price" key={billingCycle}>
                  ${row.monthly_price}
                  <span>{billingCycle === "year" ? t("billing_per_mo_billed_annually") : t("billing_per_month_slash")}</span>
                </div>
              )}

              <div className="rk-plan-limits">
                <div className="rk-plan-limit">
                  <FontAwesomeIcon icon={faComments} /><span>{t(creditsLabel ? "billing_limit_ai_credits_month" : "billing_limit_messages_month")}</span><strong>{messages}</strong>
                </div>
                <div className="rk-plan-limit">
                  <FontAwesomeIcon icon={faBookOpen} /><span>{t("billing_limit_knowledge_sources")}</span><strong>{docs}</strong>
                </div>
                <div className="rk-plan-limit">
                  <FontAwesomeIcon icon={faPuzzlePiece} /><span>{t("billing_limit_skills")}</span><strong>{skillCount}</strong>
                </div>
                <div className="rk-plan-limit">
                  <FontAwesomeIcon icon={faSliders} /><span>{t("billing_limit_custom_behaviors")}</span><strong>{behaviors}</strong>
                </div>
              </div>

              {label && (
                <button
                  type="button"
                  className={`rk-plan-cta ${ctaVariant}`}
                  disabled={ctaDisabled}
                  onClick={action}
                >
                  {pending === cardKey ? <FontAwesomeIcon icon={faSpinner} spin /> : label}
                </button>
              )}

              {isCurrentTier && onPaidStripe && (
                isCancelling ? (
                  // Cancelled but still active until period end: show when it
                  // ends and let the user reactivate (un-cancel) via the portal.
                  <>
                    <div className="rk-plan-ending">
                      <FontAwesomeIcon icon={faCircleExclamation} />
                      <span>{t("billing_plan_cancelled")}{endsText ? ` · ${endsText}` : ""}</span>
                    </div>
                    <button
                      type="button"
                      className="rk-plan-cta featured rk-plan-cancel"
                      onClick={() => onPortal(`reactivate:${name}`)}
                      disabled={busy}
                    >
                      {pending === `reactivate:${name}` ? <FontAwesomeIcon icon={faSpinner} spin /> : t("billing_reactivate_plan")}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className={`rk-plan-cta danger${label ? " rk-plan-cancel" : ""}`}
                    onClick={() => onPortal(`cancel:${name}`)}
                    disabled={busy}
                  >
                    {pending === `cancel:${name}` ? <FontAwesomeIcon icon={faSpinner} spin /> : t("billing_cancel_plan")}
                  </button>
                )
              )}

              <div className="rk-plan-div" />
              <div className="rk-plan-sec"><span>{t("billing_section_apps")}</span></div>
              <LogoRow items={APPS} lead={APP_ANY} />

              <div className="rk-plan-div" />
              <div className="rk-plan-sec"><span>{t("billing_section_channels")}</span></div>
              <LogoRow items={CHANNELS} lead={CHANNEL_ANY} />

              <div className="rk-plan-div" />
              <ul className="rk-plan-feats">
                <li>
                  <FontAwesomeIcon icon={faCheck} className="rk-feat-ok" /> {t("billing_feat_auto_lead_capture")}
                </li>
                <li className={rescrape !== "none" ? "" : "off"}>
                  <FontAwesomeIcon icon={rescrape !== "none" ? faCheck : faMinus} className={rescrape !== "none" ? "rk-feat-ok" : ""} />
                  {rescrape === "daily"
                    ? t("billing_feat_rescraping_daily")
                    : rescrape === "monthly"
                    ? t("billing_feat_rescraping_monthly")
                    : t("billing_feat_rescraping")}
                </li>
                <li className={removeBranding ? "" : "off"}>
                  <FontAwesomeIcon icon={removeBranding ? faCheck : faMinus} className={removeBranding ? "rk-feat-ok" : ""} />
                  {t("billing_feat_remove_branding_lc")}
                </li>
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}
